<template>
  <div class="tags-wrapper" v-click-outside="deselectAll" @keydown.esc="deselectAll" tabindex="0">
    <div v-if="statusDot" class="dot__status">
      x: <input class="form-control" type="number" v-model="statusDot.x"> y: <input class="form-control" type="number" v-model="statusDot.y">
    </div>

    <div ref="canvasWrapper" :style="bgStyle">
      <div class="tag-layer">
        <template v-for="tag in tags" tag="div">
          <tag-action
            v-if="tag.type === 'action'"
            :key="tag._id"
            v-bind="tag"
            :class="{
              active: selectedTag._id === tag._id, 
              inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
            }"
            @pointerdown.native="handleDragStart(tag, $event)"
            @pointerup.native="handleDragEnd($event)"
            @pointermove.native="handleDrag($event)"
          />

          <tag-basic
            v-if="tag.type === 'basic'"
            :key="tag._id"
            v-bind="tag"
            :class="{
              active: selectedTag._id === tag._id, 
              inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
            }"
            @pointerdown.native="handleDragStart(tag, $event)"
            @pointerup.native="handleDragEnd($event)"
            @pointermove.native="handleDrag($event)"
          />

          <tag-box
            v-if="tag.type === 'box'"
            :key="tag._id"
            v-bind="tag"
            :class="{
              active: selectedTag._id === tag._id, 
              inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
            }"
            @pointerdown.native="handleDragStart(tag, $event)"
            @pointerup.native="handleDragEnd($event)"
            @pointermove.native="handleDrag($event)"
          />

          <tag-reference
            v-if="tag.type === 'reference'"
            :key="tag._id"
            v-bind="tag"
            :class="{
              active: selectedTag._id === tag._id, 
              inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
            }"
            @pointerdown.native="handleDragStart(tag, $event)"
            @pointerup.native="handleDragEnd($event)"
            @pointermove.native="handleDrag($event)"
          />

          <tag-rich
            v-if="tag.type === 'rich'"
            :key="tag._id"
            v-bind="tag"
            :ref="tag._id"
            :class="{
              active: selectedTag._id === tag._id, 
              inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
            }"
            @pointerdown.native="handleDragStart(tag, $event)"
            @pointerup.native="handleDragEnd($event)"
            @pointermove.native="handleDrag($event)"
          />

          <tag-reveal
            v-if="tag.type === 'reveal'"
            :key="tag._id"
            v-bind="tag"
            :ref="tag._id"
            :class="{
              active: selectedTag._id === tag._id, 
              inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
            }"
            @pointerdown.native="handleDragStart(tag, $event)"
            @pointerup.native="handleDragEnd($event)"
            @pointermove.native="handleDrag($event)"
          />

          <tag-basic-icon
            v-if="tag.type === 'basicIcon'"
            :key="tag._id"
            v-bind="tag"
            :ref="tag._id"
            :class="{
              active: selectedTag._id === tag._id, 
              inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
            }"
            @pointerdown.native="handleDragStart(tag, $event)"
            @pointerup.native="handleDragEnd($event)"
            @pointermove.native="handleDrag($event)"
          />
        </template>
      </div>

      <reference-tag-slider 
        v-if="referenceTagsSlider.length > 0"
        :isVertical="true"
        :scaledAmount="scale"
        :tagRef="referenceTagsSlider"
        @click.native.stop="{}"
      />
    </div>

    <tag-info-box v-model="selectedTag" :lang="activeLanguage.code" @remove="removeTag" :scale="scale" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import InfoBox from '@/components/InfoBox'
import ReferenceTagSlider from '@/components/ReferenceTagSlider'
import TagAction from '@/components/TagAction'
import TagBasic from '@/components/TagBasic'
import TagBasicIcon from '@/components/TagBasicIcon'
import TagBox from '@/components/TagBox'
import TagFeatureText from '@/components/TagFeatureText'
import TagReveal from '@/components/TagReveal'
import TagRich from '@/components/TagRich'
import TagReference from '@/components/TagReference'
import TagInfoBox from '@/components/TagInfoBox'

export default {
  props: ['value'],

  components: {
    InfoBox,
    ReferenceTagSlider,
    TagAction,
    TagBasic,
    TagBasicIcon,
    TagBox,
    TagFeatureText,
    TagReveal,
    TagRich,
    TagReference,
    TagInfoBox
  },

  data () {
    return {
      scale: 1,

      isTagDraggable: false,
      isTagDragging: false,
      selectedTag: {},
      
      statusDot: null,
    }
  },

  computed: {
    ...mapGetters([
      'activeLanguage',
    ]),

    tags: {
      get () {
        return this.value
      }, 
      set (tags) {
        this.$emit('input', tags)
      }
    },

    bgStyle () {
      if (this.scale) {
        return {
          ['font-size']: (20 * this.scale) + 'px',
          ['height']: (1960 * this.scale) + 'px',
        }
      }
      return {}
    },

    referenceTagsSlider () {
      if (this.tags) {
        let refTags = this.tags.filter(tag => tag.type == 'reference')

        refTags = _.orderBy(refTags, ['pos.x'], ['asc'])

        if (this.dir === 'rtl') {
          let startingIndex = refTags.length

          refTags.forEach((tag, index) => {
            if (!tag.hideTagReference) {
              tag.referenceIndex = startingIndex--
            }
          })
          return refTags
        } else {
          refTags.forEach((tag, index) => {
            if (!tag.hideTagReference) {
              tag.referenceIndex = index + 1
            }
          })
          return refTags
        }
      } else {
        return []
      }
    }
  },

  mounted() {
    window.addEventListener('resize', this.getScaledAmount)
    this.$nextTick(() => {
      this.getScaledAmount()
    })

    // add event listener for esc key
    window.addEventListener('keydown', this.onKeyDown)
  },

  methods: {
    onKeyDown (e) {
      if (this.statusDot) {
        e.preventDefault()
      
        switch (e.keyCode) {
          case 27:
            this.clearSelection()
            break

          case 38:
            this.statusDot.y--
            break

          case 40:
            this.statusDot.y++
            break

          case 37:
            this.statusDot.x--
            break

          case 39:
            this.statusDot.x++
            break
        
          default:
            break
        }
      }
    },

    getScaledAmount() {
      // default size based on 3040x1960 - scale = 1
      if (this.$refs.canvasWrapper) {
        let canvasWidth = this.$refs.canvasWrapper.getBoundingClientRect().width
        let scaled = canvasWidth / 3190
        this.scale = scaled
      }
    },

    clearSelection () {
      this.statusDot = null
      this.selectedTag = {}
      this.isTagDragging = false
      this.isTagDraggable = false
    },

    handleDragStart (tag, e) {
      e.target.setPointerCapture(e.pointerId)

      this.isTagDraggable = true
      this.selectedTag = tag
      this.statusDot = null
    },

    handleDrag(event) {
      if (this.isTagDraggable) {
        this.isTagDragging = true

        this.selectedTag.pos.x = Math.round(this.selectedTag.pos.x + event.movementX / this.scale)
        this.selectedTag.pos.y = Math.round(this.selectedTag.pos.y + event.movementY / this.scale)

        // set dragging bounds
        const canvasWidth = this.$refs.canvasWrapper.offsetWidth
        const canvasHeight = this.$refs.canvasWrapper.offsetHeight
        const elWidth = event.target.offsetWidth / this.scale
        const elHeight = event.target.offsetHeight / this.scale

        if (this.selectedTag.pos.x < 0) {
          this.selectedTag.pos.x = 0
        }

        if (this.selectedTag.pos.x > (canvasWidth / this.scale - elWidth)) {
          this.selectedTag.pos.x = Math.round(canvasWidth / this.scale) - elWidth
        }

        if (this.selectedTag.pos.y < 0) {
          this.selectedTag.pos.y = 0
        }

        if (this.selectedTag.pos.y > (canvasHeight / this.scale) - elHeight) {
          this.selectedTag.pos.y = Math.round(canvasHeight / this.scale) - elHeight
        }
      }
    },

    handleDragEnd(e) {
      e.target.releasePointerCapture(e.pointerId)

      this.isTagDragging = false
      this.isTagDraggable = false
    },

    addTag () {
      this.$store.dispatch('GET_TAG_SCHEMA').then(res => {
        this.tags.push(res.data)
      })
    },

    deselectAll () {
      this.isTagSelected = false
      this.selectedTag = {}
    },

    removeTag () {
      const selectedTagId = this.selectedTag._id

      // remove tag from livingOption
      this.selectedTag = {}

      let remainingTags = this.tags.filter(function(tag) {
        if (tag._id != selectedTagId) {
          return tag
        }
      })

      this.tags = remainingTags
    },

    openAssetLibrary(context, assetSelected) {
      this.$emit('openAssetLibrary', context, assetSelected)
    },
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('resize', this.getScaledAmount)
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
@import '@/styles/_mixins.scss';

.tags-wrapper {
  bottom: 0;
  left: 0;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: 0;
}

.tag-layer {
  height: 100%;
  pointer-events: none;
  position: absolute;
  width: 100%;
  z-index: 10;

  &--inactive {
    opacity: .5;
  }
}
</style>
